// Every request the app makes to Supabase goes through the fetch built here:
// the auth client's sign-in renewal and sign-in calls, every table read and
// write, and the upload. Two verifier findings on PR #9 (2026-09-22) shaped it,
// both measured against supabase-js 2.116:
//
//   - Nothing had a time limit. Every query first waits for the sign-in to be
//     renewed, and the renewal had no limit either, so in a dead zone a screen
//     sat on "Loading" with nothing to press until the browser gave up.
//   - A renewal that failed left no session, and queries then went out as
//     anonymous. The database answers those with nothing, and the venue list
//     showed "No venues yet" as if it were true.

import { isAuthRefreshDiscardedError, isAuthRetryableFetchError } from "@supabase/supabase-js";
import type { Session, SupabaseClient, SupabaseClientOptions } from "@supabase/supabase-js";

/** Milliseconds each kind of request may take, from sending to the last byte. */
export interface Limits {
  /** One attempt at renewing the sign-in; see LIMITS for why it is long. */
  refresh: number;
  /** Any other auth call: send a code, verify it, sign out. */
  auth: number;
  /** A database read. */
  read: number;
  /** A database write, or any function call other than the upload. */
  write: number;
  /** The upload (`sync_batch`): this, plus `uploadPerKb` for each KB sent. */
  uploadBase: number;
  uploadPerKb: number;
}

export const LIMITS: Limits = {
  // One attempt per window. auth-js retries a failed renewal on its own, but
  // only while the next try can start within 30 s of the first, and every try
  // sends the same refresh token. A shorter limit cut slow but working answers
  // short and made it resend that token: once the server has used it, a resend
  // arriving outside its short grace period for reuse (Supabase's default is
  // 10 s; this project's is not known) is refused as "already used", and
  // auth-js then signs the person out. At 30 s a first try that times out is
  // the only one in its window, and an answer that takes up to 30 s still
  // lands. What remains: after its 60 s pause auth-js tries again with the same
  // token, so a renewal the server carried out but whose answer never arrived
  // (or took over 30 s) is still refused then; the code with no limit lost the
  // sign-in the same way to an answer that never arrived. A connection reset
  // or a 5xx inside the window is resent at once, as auth-js always has.
  // Screens do not wait on any of this: they have their own 15 s cut-off
  // (withDeadline, firstSession).
  refresh: 30_000,
  // Not retried by the library, and a verify cut short can use up the one-time
  // code, so these get longer.
  auth: 30_000,
  // Safe to repeat. The first loads on screen have their own 15 s cut-off too.
  read: 15_000,
  // A write cut short may still land, and a second tap could then duplicate it.
  write: 30_000,
  // The upload must still finish on slow signal: 60 s, plus one second per KB,
  // which a link of about 8 kbps sustains. A two-hour session sent in one go is
  // about 80 KB and gets about 140 s. It is not left unlimited, because drain is
  // single flight per session: an upload that never ends holds every later
  // attempt behind it, and nothing uploads until the app is reloaded.
  // A timeout prunes nothing (sync.ts prunes only on an applied answer), and
  // resending is safe because every row is idempotent on the server.
  uploadBase: 60_000,
  uploadPerKb: 1_000,
};

/** The cut-off on a screen's first load, whatever the sign-in renewal is doing. */
export const FIRST_LOAD_MS = 15_000;

/** A database request that was not sent because no one is signed in. */
export const NO_SESSION = "NO_SESSION";
/** A request that got no complete answer within its limit. */
export const TIMED_OUT = "CLIENT_TIMEOUT";

const SIGNAL = "Move somewhere with signal and try again.";
export const NO_SESSION_MESSAGE = `Your sign-in could not be renewed. ${SIGNAL}`;
export const RENEW_MESSAGE =
  "The sign-in on this phone needs renewing and the server did not answer. Move somewhere with signal: " +
  "it carries on by itself once it gets through, which can take a minute.";
const timedOutMessage = (ms: number) => `No answer within ${Math.round(ms / 1000)} seconds. ${SIGNAL}`;

// Statuses whose Response must be built with no body.
const NO_BODY = new Set([101, 103, 204, 205, 304]);

const urlOf = (input: RequestInfo | URL) =>
  typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

const bytesOf = (body: unknown) => (typeof body === "string" ? new TextEncoder().encode(body).byteLength : 0);

/** How long one request may take. */
export function limitFor(url: string, method: string, body: unknown, limits: Limits = LIMITS): number {
  const u = new URL(url);
  if (u.pathname.includes("/auth/v1/")) {
    return u.pathname.endsWith("/token") && u.searchParams.get("grant_type") === "refresh_token"
      ? limits.refresh : limits.auth;
  }
  if (u.pathname.endsWith("/rest/v1/rpc/sync_batch")) {
    return limits.uploadBase + Math.ceil(bytesOf(body) / 1024) * limits.uploadPerKb;
  }
  return method === "GET" || method === "HEAD" ? limits.read : limits.write;
}

// A made-up answer, in the shape both libraries read errors in. Why an answer
// and not a thrown error, and why these statuses:
//   - postgrest-js retries a GET whose fetch throws (after 1, 2 and 4 s), which
//     would multiply every timeout; it does not retry a 401 or a 504.
//   - auth-js reads a 4xx on a renewal as a rejected refresh token and deletes
//     the session, which signs the person out. A 5xx is on its list of
//     failures to retry, so a timeout never costs anyone their sign-in.
function answer(status: 401 | 504, code: string, message: string): Response {
  return new Response(JSON.stringify({ code, message, details: null, hint: null }), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * The fetch the whole client uses. `send` is looked up at call time, so it is
 * whatever `fetch` is when the request leaves.
 */
export function boundedFetch(
  key: string,
  limits: Limits = LIMITS,
  send: typeof fetch = (input, init) => fetch(input, init),
): typeof fetch {
  return async (input, init = {}) => {
    // A caller that has already given up (a screen's cut-off fired while the
    // sign-in was being renewed) gets nothing sent on its behalf, so a late
    // write cannot land after the screen said it failed.
    const caller = init.signal ?? undefined;
    if (caller?.aborted) throw caller.reason ?? new DOMException("The request was cancelled.", "AbortError");

    const url = urlOf(input);
    const method = (init.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();

    // Without a signed-in user's token a database request is not sent at all.
    // It would go out as anonymous, and the database answers that with no rows,
    // which a screen cannot tell from a real empty list. Nothing in this app
    // reads the database signed out.
    if (new URL(url).pathname.includes("/rest/v1/")) {
      const bearer = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined)).get("Authorization");
      // Compared without surrounding spaces: headers are trimmed on the way in,
      // and a key built with a stray newline must not slip past as a sign-in.
      const token = bearer?.replace(/^Bearer\s+/i, "").trim();
      if (!token || token === key.trim()) return answer(401, NO_SESSION, NO_SESSION_MESSAGE);
    }

    const ms = limitFor(url, method, init.body, limits);
    const stop = new AbortController();
    let late = false;
    const timer = setTimeout(() => { late = true; stop.abort(); }, ms);
    const relay = () => stop.abort(caller?.reason);
    caller?.addEventListener("abort", relay, { once: true });
    // Settles only when the request is given up on, so the limit holds even
    // against a transport that does not end its own work on abort.
    const gaveUp = new Promise<never>((_, reject) => {
      stop.signal.addEventListener("abort", () => reject(stop.signal.reason), { once: true });
    });
    gaveUp.catch(() => undefined);
    try {
      const res = await Promise.race([send(input, { ...init, signal: stop.signal }), gaveUp]);
      // The body is read here, inside the limit. A connection can stall after
      // the headers arrive, and the libraries' own read of the body has no limit.
      const body = NO_BODY.has(res.status) ? null : await Promise.race([res.arrayBuffer(), gaveUp]);
      return new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
    } catch (e) {
      if (late) return answer(504, TIMED_OUT, timedOutMessage(ms));
      throw e;
    } finally {
      clearTimeout(timer);
      caller?.removeEventListener("abort", relay);
    }
  };
}

type AuthOptions = NonNullable<SupabaseClientOptions<"public">["auth"]>;

/**
 * Options for createClient. The app passes only the key; tests and the Node
 * harness also pass a transport, shorter limits or their own auth storage.
 */
export function clientOptions(key: string, over: { limits?: Limits; send?: typeof fetch; auth?: AuthOptions } = {}) {
  return {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, ...over.auth },
    global: { fetch: boundedFetch(key, over.limits, over.send) },
  } satisfies SupabaseClientOptions<"public">;
}

/** The answer a load gets when its cut-off fires first. */
export interface Late {
  data: null;
  error: { code: typeof TIMED_OUT; message: string; details: string; hint: string };
  status: 0;
  statusText: "";
  count: null;
}

/**
 * Runs a screen's load against a cut-off that also covers the sign-in renewal
 * every query waits on first, which no limit on a single request can: auth-js
 * spends about 30 s retrying a renewal before any query leaves. `run` builds the
 * query with `.abortSignal(signal)`. When the cut-off fires, the query is
 * cancelled; if it had not been sent yet, it never is.
 */
export async function withDeadline<T>(run: (signal: AbortSignal) => PromiseLike<T>, ms = FIRST_LOAD_MS): Promise<T | Late> {
  const stop = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cutOff = new Promise<Late>((resolve) => {
    timer = setTimeout(() => {
      stop.abort();
      resolve({
        data: null,
        error: { code: TIMED_OUT, message: timedOutMessage(ms), details: "", hint: "" },
        status: 0, statusText: "", count: null,
      });
    }, ms);
  });
  try {
    return await Promise.race([run(stop.signal), cutOff]);
  } finally {
    clearTimeout(timer);
  }
}

/** Signed in (or not), or a sign-in that is there but could not be renewed. */
export type FirstSession = { session: Session | null } | { retry: string };

/**
 * The session the app starts with. auth-js reports a renewal that failed for
 * want of signal the same way as no sign-in at all (its INITIAL_SESSION event
 * carries null for both), and the app used to show the sign-in screen to
 * someone still signed in. This tells them apart, and does not wait on a
 * renewal longer than a first load would.
 */
export async function firstSession(auth: Pick<SupabaseClient["auth"], "getSession">, ms = FIRST_LOAD_MS): Promise<FirstSession> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cutOff = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), ms); });
  try {
    const got = await Promise.race([auth.getSession(), cutOff]);
    if (got === null) return { retry: RENEW_MESSAGE };
    // A renewal that failed for want of signal, or one set aside because
    // another tab renewed the same sign-in first: either way it is still there.
    if (got.error && (isAuthRetryableFetchError(got.error) || isAuthRefreshDiscardedError(got.error))) {
      return { retry: RENEW_MESSAGE };
    }
    // Any other error means the sign-in was refused, and auth-js has already
    // removed it: that person is signed out.
    return { session: got.data.session };
  } catch {
    return { retry: RENEW_MESSAGE };
  } finally {
    clearTimeout(timer);
  }
}

const OFFLINE = /failed to fetch|networkerror|load failed|network request failed/i;

/**
 * Whether a failed load can clear up on its own: no signal, a stall, a gateway
 * fault, or a sign-in that could not be renewed. A refusal will not.
 */
export function canClearUp(error: { message: string; code?: string }, status = 0): boolean {
  return error.code === NO_SESSION || error.code === TIMED_OUT || error.code === EMPTY_ANSWER
    || status >= 500 || OFFLINE.test(error.message);
}

/** A success that came back with no body: not a list, and not an empty one. */
export const EMPTY_ANSWER = "EMPTY_ANSWER";
export const emptyAnswer = { code: EMPTY_ANSWER, message: `The server sent back nothing. ${SIGNAL}` };
